import { AntDesign, Ionicons } from "@expo/vector-icons";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import {
  Button,
  Divider,
  Page,
  TextButton,
  TextField,
  Tooltip,
} from "@towersystems/roam-common-native";
import React from "react";
import { Colors, Spacings, Text, View } from "react-native-ui-lib";
import { useActiveLocation } from "../../../../utilities/app-state/hooks";
import { ProductSummaryCard } from "../../../products/components";
import { fetchSaleKitItemDetails } from "../../utils/sale-item/utils";
import { useActiveCartItem } from "../CartItemModalRootScreen/hooks";
import { OtherListCard } from "./components/OtherListCard";
import { PricingListCard } from "./components/PricingListCard";
import { EditCartItemModalScreenProps, ProductState } from "./types";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

export const EditCartItemModalScreen = (
  props: EditCartItemModalScreenProps
) => {
  const { navigation } = props;
  const { location } = useActiveLocation();
  const { strategyEnabled } = useLlmQuickWinsProvider();

  const database = useDatabase();

  const [productState, setProductState] = React.useState<
    ProductState | undefined
  >(undefined);

  const { item } = useActiveCartItem();
  const [value, setValue] = React.useState(item.quantity.toString());

  React.useEffect(() => {
    item.product.fetch().then(async (product) => {
      if (!product.isKit) {
        setProductState({ product });
        return;
      }

      const kit = await product.productKit.fetch();

      const kitItemDetails = await fetchSaleKitItemDetails(database, kit, item);

      setProductState({
        product,
        productKit: kit,
        kitItemDetails,
      });
    });
  }, []);
  React.useEffect(() => {
    const now = new Date();
    const localMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 30,
      0,
      0,
      0,
      0
    );

    const utcTimeForLocalMidnight = localMidnight.toISOString();

    navigation.setOptions({
      headerTitle(props) {
        if (strategyEnabled("product_summary_quick_win")) {
          return (
            <View>
              <Tooltip text="Generate AI insights using the last 30 days of sales data">
                <TextButton
                  onPress={() => {
                    navigation.navigate("ProductAiInsightsRootModalScreen", {
                      screen: "ProductAiInsightsScreen",
                      productId: item.product.id,
                      startDate: utcTimeForLocalMidnight,
                    });
                  }}
                  icon={<Ionicons name="sparkles" size={12} />}
                >
                  AI Insights
                </TextButton>
              </Tooltip>
            </View>
          );
        } else {
          return <View />;
        }
      },
    });
  }, []);

  const isDirty = React.useMemo(() => {
    return value !== item.quantity.toString();
  }, [value, item.quantity]);

  const isValid = React.useMemo(() => {
    const num = Number(value);
    return /^[-+]?(\d+(\.\d*)?|\.\d+)([eE][-+]?\d+)?$/.test(value) && num !== 0;
  }, [value]);

  const handleOnSave = React.useCallback(async () => {
    if (!isValid || !isDirty) {
      return;
    }
    const quantity = parseFloat(value);
    if (quantity === item.quantity) {
      return;
    }

    return item
      .updateQuantity(quantity)
      .then(() => navigation.pop())
      .catch((e) => console.log(e));
  }, [isValid, isDirty, value, item]);

  const handleOnRemove = async () => {
    navigation.goBack();
    return item.deleteItem().catch((e) => console.log(e));
  };

  const showSaveButtons = isDirty;
  const disabled = !isDirty || !isValid;

  const footer = showSaveButtons ? (
    <Button
      label={"Save"}
      backgroundColor={Colors.action}
      color="white"
      disabled={disabled}
      onPress={handleOnSave}
    ></Button>
  ) : (
    <View row>
      <View marginR-10 flex>
        <Button
          isDestructive
          onPress={handleOnRemove}
          label="Remove Item"
        ></Button>
      </View>
      <View flex>
        <Button
          onPress={() => navigation.goBack()}
          isAction
          label="Return to Cart"
        ></Button>
      </View>
    </View>
  );

  if (!productState) {
    return null;
  }

  return (
    <Page footer={footer}>
      {location && !!productState ? (
        <ProductSummaryCard
          productId={productState.product.id}
          locationId={location.id}
          onPress={() =>
            navigation.navigate("EditProductRootScreen", {
              productId: productState.product.id,
              screen: "EditProductScreen",
            })
          }
        />
      ) : null}

      <Divider />
      {productState.product.adjustable ? (
        <View>
          <View style={{ marginBottom: Spacings.s5 }}>
            <Text
              style={[
                {
                  textTransform: "uppercase",
                  fontWeight: "bold",
                },
              ]}
            >
              Quantity
            </Text>
          </View>
          <View row>
            <View marginR-30 flex>
              <Button
                disabled={!isValid}
                onPress={() => {
                  if (value === "1") {
                    setValue("-1");
                  } else if (isValid) {
                    let newValue = parseInt(value);
                    newValue = newValue - 1;
                    setValue(newValue.toString());
                  }
                }}
              >
                <AntDesign
                  name="minus"
                  size={24}
                  color={!isValid ? Colors.white : Colors.action}
                />
              </Button>
            </View>
            <View marginR-30 style={{ flex: 1 }}>
              <TextField
                placeholder="quantity"
                fieldStyle={{ height: 60, flex: 1, flexDirection: "column" }}
                style={{ textAlign: "center", flex: 1 }}
                value={value}
                keyboardType="numeric"
                onChangeText={(v) => setValue(v)}
                validationMessage={
                  isValid ? undefined : "Please a valid number"
                }
              />
            </View>
            <View flex>
              <Button
                disabled={!isValid}
                onPress={() => {
                  if (value === "-1") {
                    setValue("1");
                  } else if (isValid) {
                    let newValue = parseInt(value);
                    newValue = newValue + 1;
                    setValue(newValue.toString());
                  }
                }}
                fullWidth
              >
                <AntDesign
                  name="plus"
                  size={24}
                  color={!isValid ? Colors.white : Colors.action}
                />
              </Button>
            </View>
          </View>
          <Divider style={{ marginTop: 60, marginBottom: 40 }} />
        </View>
      ) : null}

      <PricingListCard
        {...props}
        item={item}
        productState={productState}
        navigation={navigation}
      />

      <OtherListCard
        {...props}
        item={item}
        productState={productState}
        navigation={navigation}
      />
    </Page>
  );
};
